import { z } from 'zod';
import { BaseSchema } from '../base/base.entity.model';
import * as FlatModels from '../../internal';

export const QuestionType = z.enum([
  'SingleChoiceQuestion',
  'LongListQuestion',
  'MappingQuestion',
  'KprimeQuestion',
  'TrueFalseQuestion',
  'FreeTextQuestion',
  'IndicationQuestion',
  'MultipleChoiceQuestion',
]);
export type QuestionType = z.infer<typeof QuestionType>;


export const QuestionTypeIcon: Record<string, string> = {
  [QuestionType.enum.LongListQuestion]: FlatModels.ButtonDesignModels.ButtonDefaultIcons.QUESTION_LONG_LIST,
  [QuestionType.enum.TrueFalseQuestion]: FlatModels.ButtonDesignModels.ButtonDefaultIcons.QUESTION_TRUE_FALSE,
  [QuestionType.enum.FreeTextQuestion]: FlatModels.ButtonDesignModels.ButtonDefaultIcons.QUESTION_FREE_TEXT,
  [QuestionType.enum.IndicationQuestion]: FlatModels.ButtonDesignModels.ButtonDefaultIcons.QUESTION_INDICATION,
  [QuestionType.enum.MappingQuestion]: FlatModels.ButtonDesignModels.ButtonDefaultIcons.QUESTION_MAPPING,
  [QuestionType.enum.SingleChoiceQuestion]: FlatModels.ButtonDesignModels.ButtonDefaultIcons.QUESTION_SINGLE_CHOICE,
  [QuestionType.enum.KprimeQuestion]: FlatModels.ButtonDesignModels.ButtonDefaultIcons.QUESTION_KPRIME,
}

export const QuestionTypeArray = Object.values(QuestionType.enum)
    .filter(value => ([QuestionType.enum.SingleChoiceQuestion, QuestionType.enum.KprimeQuestion, QuestionType.enum.MultipleChoiceQuestion] as string[]).indexOf(value) === -1)
    .map(value => { return { value: value, name: 'enum.question.' + value }; });

export enum NumberingType {
  NUMBER = '1',
  LETTER = 'A',
  ROMAN = 'I',
  NONE = 'None'
}

export const NumberingTypeArray = Object.entries(NumberingType)
    .map(value => { return { value: value[1], name: 'enum.question.' + value[0] }; });

export const QuestionSchema = BaseSchema.extend({
  slide: z.string().uuid(),
  choices: z.array(z.string().uuid()).default([]),
  order: z.number(),
  customEnumeration: z.string().nullable(),
  content: z.string(),
  placeholder: z.string().max(255).nullable().default(null),
  explanation: z.string().nullable().default(null),
  required: z.boolean().default(false),
  randomised: z.boolean().default(false),
  numberingType: z.nativeEnum(NumberingType).default(NumberingType.LETTER),
  type: QuestionType,
});

export type Question = z.infer<typeof QuestionSchema>;

export const SingleChoiceQuestionSchema = QuestionSchema.extend({
  type: z.literal('SingleChoiceQuestion'),
  solution: z.number().nullable().default(null),
});

export type SingleChoiceQuestion = z.infer<typeof SingleChoiceQuestionSchema>;

export const LongListQuestionSchema = QuestionSchema.extend({
  type: z.literal('LongListQuestion'),
  solution: z.number().nullable().default(null),
});

export type LongListQuestion = z.infer<typeof LongListQuestionSchema>;

export const MappingQuestionSolutionSchema = z.object({
  solution: z.number().nullable().default(null),
});

export const MappingQuestionSchema = QuestionSchema.extend({
  type: z.literal('MappingQuestion'),
  solutions: z.array(z.string()).default([]),
});

export type MappingQuestion = z.infer<typeof MappingQuestionSchema>;

export const KPrimeQuestionSolutionArray = [
  { value: '1', name: 'enum.question.true' },
  { value: '0', name: 'enum.question.false' }
];

export const KprimeQuestionSolutionSchema = z.object({
  solution: z.string()
});

export const KprimeQuestionSchema = QuestionSchema.extend({
  type: z.literal('KprimeQuestion'),
  solutions: z.array(z.string()).default([]),
});

export type KprimeQuestion = z.infer<typeof KprimeQuestionSchema>;

export const TrueFalseQuestionSolutionArray = [
  { value: 1, name: 'enum.question.true' },
  { value: 0, name: 'enum.question.false' }
];

export const TrueFalseQuestionSchema = QuestionSchema.extend({
  type: z.literal('TrueFalseQuestion'),
  solutions: z.array(z.string()).default([]),
});

export type TrueFalseQuestion = z.infer<typeof TrueFalseQuestionSchema>;

export const FreeTextQuestionSchema = QuestionSchema.extend({
  type: z.literal('FreeTextQuestion'),
  solutionText: z.string().nullable().default(null),
});

export type FreeTextQuestion = z.infer<typeof FreeTextQuestionSchema>;

export const IndicationQuestionSchema = QuestionSchema.extend({
  type: z.literal('IndicationQuestion'),
  solution: z.number().nullable().default(null),
});

export type IndicationQuestion = z.infer<typeof IndicationQuestionSchema>;

export const MultipleChoiceQuestionSchema = QuestionSchema.extend({
  type: z.literal('MultipleChoiceQuestion'),
  solutions: z.array(z.string()).default([]),
});

export type MultipleChoiceQuestion = z.infer<typeof MultipleChoiceQuestionSchema>;

export const QuestionEntityTypeSchema = z.discriminatedUnion('type', [
  SingleChoiceQuestionSchema,
  LongListQuestionSchema,
  MappingQuestionSchema,
  KprimeQuestionSchema,
  TrueFalseQuestionSchema,
  FreeTextQuestionSchema,
  IndicationQuestionSchema,
  MultipleChoiceQuestionSchema,
]);

export type QuestionEntityType = z.infer<typeof QuestionEntityTypeSchema>;

export const QuestionEntityQueryResultSchema = FlatModels.EntityControlModels.EntityQueryResultSchema.extend({
  entity: QuestionEntityTypeSchema,
});

export type QuestionEntityQueryResult = z.infer<typeof QuestionEntityQueryResultSchema>;

export const ShowRandomized = [
  'LongListQuestion',
  'SingleChoiceQuestion',
  'TrueFalseQuestion',
  'KprimeQuestion',
  'MappingQuestion'
];

export const ShowNumberingType = [
  'LongListQuestion',
  'SingleChoiceQuestion',
  'TrueFalseQuestion',
  'KprimeQuestion'
];

export const generateCustomEnumerationTypeSchema = z.object({
  type: z.nativeEnum(NumberingType),
});

export type generateCustomEnumerationType = z.infer<typeof generateCustomEnumerationTypeSchema>;
